import type { CatalogEntityId } from '@/types/data/catalog';
import type {
  DatePreset,
  LogEventFilter,
} from '@/types/data/catalog-log-event';
import type { PageRequest } from '@/types/shared/page';
import type { UUID } from '@/utils/uuid';

/**
 * Stable filter shape for query keys. For live presets, we key on the preset
 * name rather than absolute timestamps (which slide forward every poll). For
 * custom mode the exact from/to are stable and safe to include.
 */
export type LogQueryKeyFilter = Omit<LogEventFilter, 'from' | 'to'> & {
  datePreset: DatePreset;
  from?: string;
  to?: string;
};

type CatalogDomain =
  | 'accounts'
  | 'persons'
  | 'groups'
  | 'roles'
  | 'licenses'
  | 'permissions'
  | 'entities';

export const catalogQueryKeys = {
  list: (
    domain: CatalogDomain,
    tenantId?: UUID,
    connectorId?: UUID,
    params?: PageRequest
  ) => ['catalog', domain, tenantId, connectorId, params] as const,
  entities: (tenantId?: UUID, connectorId?: UUID) =>
    ['catalog', 'entities', tenantId, connectorId] as const,
  genericEntityList: (
    tenantId?: UUID | null,
    connectorId?: UUID | null,
    entityId?: CatalogEntityId,
    query?: PageRequest
  ) => ['catalog', tenantId, connectorId, entityId, query] as const,
  tenantConnectorRoot: (tenantId?: UUID, connectorId?: UUID) =>
    ['catalog', tenantId, connectorId] as const,
  log: (tenantId?: UUID, connectorId?: UUID, filter?: LogQueryKeyFilter) =>
    ['catalog', 'log', tenantId, connectorId, filter] as const,
  tenantLog: (tenantId?: UUID, filter?: LogQueryKeyFilter) =>
    ['catalog', 'tenant-log', tenantId, filter] as const,
  entityHistory: (tenantId?: UUID, targetRef?: string) =>
    ['catalog', 'entity-history', tenantId, targetRef] as const,
};
